/**
 * My Designs — cloud (Shopify metaobject) or local fallback.
 */
(function () {
  const Core = window.BraceletCore;
  const Cloud = () => window.DesignCloudApi;
  if (!Core) return;

  const root = document.getElementById('my-designs-root');
  if (!root) return;

  const cfg = Core.CONFIG();
  const designerUrl = (root.dataset.designerUrl || cfg.designerUrl || '/pages/designer').replace(/\/?$/, '');
  const designerSep = designerUrl.includes('?') ? '&' : '?';
  const detailBase = (cfg.designDetailUrl || root.dataset.detailUrl || '/?view=design-detail').replace(
    /\/?$/,
    ''
  );
  const detailSep = cfg.designDetailSep || (detailBase.includes('?') ? '&' : '?');
  const useCloud = Cloud()?.enabled?.() === true;

  const els = {
    grid: root.querySelector('[data-md-grid]'),
    empty: root.querySelector('[data-md-empty]'),
    guestBanner: root.querySelector('[data-md-guest-banner]'),
    loginRequired: root.querySelector('[data-md-login-required]'),
  };

  const i18n = {
    add: root.dataset.i18nAdd || 'Add new design',
    empty: root.dataset.i18nEmpty || 'No saved designs yet.',
    deleteTitle: root.dataset.i18nDeleteTitle || 'Delete design?',
    deleteMessage: root.dataset.i18nDeleteMessage || 'This cannot be undone.',
    deleteMenu: root.dataset.i18nDeleteMenu || 'Delete',
    deleteConfirm: root.dataset.i18nDeleteConfirm || 'Delete',
    deleteCancel: root.dataset.i18nDeleteCancel || 'Cancel',
    deleteError: root.dataset.i18nDeleteError || 'Could not delete. Try again.',
    singleTag: root.dataset.i18nSingleTag || 'Single bead',
    shared: root.dataset.i18nShared || 'Shared',
    publish: root.dataset.i18nPublish || 'Share to plaza',
    unpublish: root.dataset.i18nUnpublish || 'Withdraw from Gallery',
    viewDetail: root.dataset.i18nViewDetail || 'View details',
    edit: root.dataset.i18nEdit || 'Edit',
    addCart: root.dataset.i18nAddCart || 'Add to Cart',
    menuLabel: root.dataset.i18nMenu || 'More actions',
    beads: root.dataset.i18nBeads || '{{ count }} beads',
    untitled: root.dataset.i18nUntitled || 'Untitled',
  };

  let openMenu = null;

  function formatUpdated(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }

  function formatMoney(n) {
    return `$${Number(n).toFixed(2)}`;
  }

  function formatWrist(cm) {
    const n = Number(cm) || 15;
    const inches = (n / 2.54).toFixed(2);
    return `${n}cm/${inches}in`;
  }

  function beadLabel(count) {
    return i18n.beads.replace('{{ count }}', String(count));
  }

  function newDesignUrl() {
    return designerUrl;
  }

  function editDesignUrl(id) {
    return `${designerUrl}${designerSep}design=${encodeURIComponent(id)}`;
  }

  function orderConfirmHref() {
    return `${detailBase}${detailSep}custom=1`;
  }

  function navigateToOrderConfirm(design, id) {
    const beads = Core.expandDesign(design.designJson);
    const wristSizeCm = design.wristSizeCm ?? design.designJson?.w ?? 15;
    const existing = Core.getDesignConfirm();
    const title =
      existing?.designId === id && existing?.title?.trim()
        ? existing.title.trim()
        : design.title?.trim() || 'My Crystal Bracelet';
    Core.saveDesignConfirm({
      title,
      wristSizeCm,
      beads,
      designJson: design.designJson || Core.compactDesignJson(beads, wristSizeCm),
      designId: id,
    });
    window.location.href = orderConfirmHref();
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const confirmRoot = document.querySelector('[data-bd-confirm]');
  let confirmPending = null;

  function setupConfirmDialog() {
    if (!confirmRoot) return null;

    const backdrop = confirmRoot.querySelector('[data-bd-confirm-backdrop]');
    const cancelBtn = confirmRoot.querySelector('[data-bd-confirm-cancel]');
    const okBtn = confirmRoot.querySelector('[data-bd-confirm-ok]');
    const titleEl = confirmRoot.querySelector('[data-bd-confirm-title]');
    const messageEl = confirmRoot.querySelector('[data-bd-confirm-message]');

    function settle(result) {
      if (!confirmPending) return;
      confirmRoot.classList.remove('is-visible');
      document.body.classList.remove('bd-confirm-open');
      const resolve = confirmPending;
      confirmPending = null;
      window.setTimeout(() => {
        confirmRoot.hidden = true;
        confirmRoot.setAttribute('aria-hidden', 'true');
        cancelBtn.hidden = false;
        confirmRoot.querySelector('.bd-confirm__actions')?.classList.remove('bd-confirm__actions--alert');
      }, 220);
      resolve(result);
    }

    cancelBtn?.addEventListener('click', () => settle(false));
    backdrop?.addEventListener('click', () => settle(false));
    okBtn?.addEventListener('click', () => settle(true));

    document.addEventListener('keydown', (e) => {
      if (confirmRoot.hidden || !confirmPending) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        settle(false);
      }
    });

    return { titleEl, messageEl, okBtn, cancelBtn };
  }

  const confirmUI = setupConfirmDialog();

  function confirmDialog({ title, message, confirmText, cancelText, variant = 'danger' }) {
    if (!confirmUI) {
      return Promise.resolve(window.confirm(`${title}\n\n${message}`));
    }

    return new Promise((resolve) => {
      confirmPending = resolve;
      confirmUI.titleEl.textContent = title;
      confirmUI.messageEl.textContent = message;
      confirmUI.okBtn.textContent = confirmText;
      confirmUI.cancelBtn.textContent = cancelText;
      confirmUI.okBtn.classList.remove('bd-confirm__btn--danger', 'bd-confirm__btn--success');
      if (variant === 'danger') {
        confirmUI.okBtn.classList.add('bd-confirm__btn--danger');
      }

      confirmRoot.hidden = false;
      confirmRoot.setAttribute('aria-hidden', 'false');
      document.body.classList.add('bd-confirm-open');
      requestAnimationFrame(() => confirmRoot.classList.add('is-visible'));
      confirmUI.cancelBtn.focus();
    });
  }

  function closeOpenMenu() {
    if (!openMenu) return;
    openMenu.menu.hidden = true;
    openMenu.btn.setAttribute('aria-expanded', 'false');
    openMenu = null;
  }

  function setupMenuDismiss() {
    if (root.dataset.mdMenuBound) return;
    root.dataset.mdMenuBound = '1';
    document.addEventListener('click', (e) => {
      if (!openMenu) return;
      if (openMenu.wrap.contains(e.target)) return;
      closeOpenMenu();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeOpenMenu();
    });
  }

  async function renderPreview(canvas, designJson) {
    const normalized = Core.normalizeDesignJson(designJson);
    const beads = Core.expandDesign(designJson);
    await Core.drawThumbnail(canvas, beads, '#f4f4f2', {
      wristCm: normalized?.wrist_size_cm,
    });
  }

  async function fetchDesigns() {
    if (useCloud) {
      await Core.ensureCustomerContext();
      if (!Core.isLoggedInCustomer()) return [];
      try {
        return await Cloud().listMine();
      } catch (err) {
        if (Cloud().isCloudUnavailableError?.(err)) {
          console.warn('[My Designs] Cloud API unavailable', err);
          return [];
        }
        throw err;
      }
    }
    await Core.mergeGuestMyDesignsIfNeeded();
    return Core.listMyDesigns();
  }

  function iconSvg(name) {
    const icons = {
      eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>',
      edit: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
      cart: '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/>',
      undo: '<path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6.2 2.3L3 13"/>',
      share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 3.5M15.4 6.5 8.6 10.5"/>',
      trash:
        '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/>',
    };
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">${icons[name] || ''}</svg>`;
  }

  function createMenuItem({ label, icon, href, danger, onClick }) {
    if (href) {
      const a = document.createElement('a');
      a.className = `my-designs__menu-item${danger ? ' is-danger' : ''}`;
      a.href = href;
      a.setAttribute('role', 'menuitem');
      a.innerHTML = `${iconSvg(icon)}<span>${escapeHtml(label)}</span>`;
      return a;
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `my-designs__menu-item${danger ? ' is-danger' : ''}`;
    btn.setAttribute('role', 'menuitem');
    btn.innerHTML = `${iconSvg(icon)}<span>${escapeHtml(label)}</span>`;
    if (onClick) btn.addEventListener('click', onClick);
    return btn;
  }

  async function addDesignToCart(design, id) {
    try {
      const beads = Core.expandDesign(design.designJson);
      await Core.addToCart({
        title: design.title || 'My Crystal Bracelet',
        beads,
        wristSizeCm: design.wristSizeCm ?? 15,
        designId: id,
      });
    } catch (e) {
      alert(e?.message || 'Could not add to cart');
    }
  }

  function buildAddCard() {
    const link = document.createElement('a');
    link.className = 'my-designs__card my-designs__card--add';
    link.href = newDesignUrl();
    link.setAttribute('role', 'listitem');
    link.innerHTML = `
      <span class="my-designs__add-icon" aria-hidden="true">+</span>
      <span class="my-designs__add-label">${escapeHtml(i18n.add)}</span>
    `;
    return link;
  }

  function buildDesignCard(design) {
    const id = design.handle || design.id;
    const card = document.createElement('article');
    card.className = 'my-designs__card my-designs__card--design';
    card.setAttribute('role', 'listitem');

    const previewLink = document.createElement('a');
    previewLink.className = 'my-designs__preview-link';
    previewLink.href = orderConfirmHref();
    previewLink.setAttribute('aria-label', design.title || i18n.untitled);
    previewLink.addEventListener('click', (e) => {
      e.preventDefault();
      navigateToOrderConfirm(design, id);
    });

    const preview = document.createElement('div');
    preview.className = 'my-designs__preview';
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 320;
    preview.appendChild(canvas);

    if (design.published) {
      const shared = document.createElement('span');
      shared.className = 'my-designs__shared-badge';
      shared.innerHTML = `${iconSvg('share')}<span>${escapeHtml(i18n.shared)}</span>`;
      preview.appendChild(shared);
    } else if (design.kind === 'single') {
      const tag = document.createElement('span');
      tag.className = 'my-designs__tag';
      tag.textContent = i18n.singleTag;
      preview.appendChild(tag);
    }

    previewLink.appendChild(preview);

    const body = document.createElement('div');
    body.className = 'my-designs__body';

    const head = document.createElement('div');
    head.className = 'my-designs__card-head';

    const title = document.createElement('h2');
    title.className = 'my-designs__name';
    title.textContent = design.title?.trim() || i18n.untitled;

    const menuWrap = document.createElement('div');
    menuWrap.className = 'my-designs__menu-wrap';

    const menuBtn = document.createElement('button');
    menuBtn.type = 'button';
    menuBtn.className = 'my-designs__menu-btn';
    menuBtn.setAttribute('aria-label', i18n.menuLabel);
    menuBtn.setAttribute('aria-expanded', 'false');
    menuBtn.setAttribute('aria-haspopup', 'menu');
    menuBtn.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>';

    const menu = document.createElement('div');
    menu.className = 'my-designs__menu';
    menu.hidden = true;
    menu.setAttribute('role', 'menu');

    menu.appendChild(
      createMenuItem({
        label: i18n.viewDetail,
        icon: 'eye',
        onClick: (e) => {
          e.preventDefault();
          closeOpenMenu();
          navigateToOrderConfirm(design, id);
        },
      })
    );
    menu.appendChild(createMenuItem({ label: i18n.edit, icon: 'edit', href: editDesignUrl(id) }));
    menu.appendChild(
      createMenuItem({
        label: i18n.addCart,
        icon: 'cart',
        onClick: async (e) => {
          e.preventDefault();
          closeOpenMenu();
          await addDesignToCart(design, id);
        },
      })
    );

    if (useCloud) {
      menu.appendChild(
        createMenuItem({
          label: design.published ? i18n.unpublish : i18n.publish,
          icon: design.published ? 'undo' : 'share',
          onClick: async (e) => {
            e.preventDefault();
            closeOpenMenu();
            try {
              await Cloud().setPublished(id, !design.published, design.sort || 0);
              await render();
            } catch {
              /* ignore */
            }
          },
        })
      );
    }

    menu.appendChild(
      createMenuItem({
        label: i18n.deleteMenu,
        icon: 'trash',
        danger: true,
        onClick: async (e) => {
          e.preventDefault();
          e.stopPropagation();
          closeOpenMenu();
          const ok = await confirmDialog({
            title: i18n.deleteTitle,
            message: i18n.deleteMessage,
            confirmText: i18n.deleteConfirm,
            cancelText: i18n.deleteCancel,
            variant: 'danger',
          });
          if (!ok) return;
          try {
            if (useCloud) {
              await Core.ensureCustomerContext();
              if (!Core.isLoggedInCustomer()) {
                alert(i18n.deleteError);
                return;
              }
              await Cloud().remove(id);
            } else {
              Core.deleteMyDesign(id);
            }
            await render();
          } catch (err) {
            console.warn('[My Designs] delete failed', err);
            alert(i18n.deleteError);
          }
        },
      })
    );

    menuBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (openMenu && openMenu.wrap === menuWrap) {
        closeOpenMenu();
        return;
      }
      closeOpenMenu();
      menu.hidden = false;
      menuBtn.setAttribute('aria-expanded', 'true');
      openMenu = { wrap: menuWrap, menu, btn: menuBtn };
    });

    menuWrap.append(menuBtn, menu);
    head.append(title, menuWrap);

    const date = document.createElement('p');
    date.className = 'my-designs__date';
    date.textContent = formatUpdated(design.updatedAt || design.createdAt);

    const footer = document.createElement('div');
    footer.className = 'my-designs__card-foot';

    const chips = document.createElement('div');
    chips.className = 'my-designs__chips';
    const beadCount = design.beadCount || Core.expandDesign(design.designJson).length || 0;
    const wristCm = design.wristSizeCm ?? design.designJson?.w ?? 15;

    chips.innerHTML = `
      <span class="my-designs__chip">${escapeHtml(beadLabel(beadCount))}</span>
      <span class="my-designs__chip">${escapeHtml(formatWrist(wristCm))}</span>
    `;

    const price =
      design.price > 0 ? formatMoney(design.price) : formatMoney(Core.calcTotal(Core.expandDesign(design.designJson)));
    const priceEl = document.createElement('p');
    priceEl.className = 'my-designs__price';
    priceEl.textContent = price;

    footer.append(chips, priceEl);
    body.append(head, date, footer);
    card.append(previewLink, body);
    renderPreview(canvas, design.designJson);

    return card;
  }

  function updateAuthUi() {
    const loggedIn = Core.isLoggedInCustomer();
    if (els.guestBanner) {
      els.guestBanner.hidden = useCloud ? loggedIn : loggedIn;
    }
    if (els.loginRequired) {
      els.loginRequired.hidden = !useCloud || loggedIn;
    }
    if (els.grid) els.grid.hidden = useCloud && !loggedIn;
    if (els.empty) els.empty.hidden = useCloud && !loggedIn;
  }

  async function render() {
    closeOpenMenu();
    await Core.ensureCustomerContext();
    updateAuthUi();

    if (useCloud && !Core.isLoggedInCustomer()) {
      if (els.grid) els.grid.replaceChildren();
      return;
    }

    const designs = await fetchDesigns();
    els.grid.replaceChildren();
    els.grid.appendChild(buildAddCard());
    for (const design of designs) {
      els.grid.appendChild(buildDesignCard(design));
    }

    const hasDesigns = designs.length > 0;
    if (els.empty) els.empty.hidden = hasDesigns || (useCloud && !Core.isLoggedInCustomer());
    updateAuthUi();
  }

  async function init() {
    setupMenuDismiss();
    root.classList.toggle('my-designs--cloud', useCloud);
    await Core.ensureCustomerContext();
    Core.debugAuth?.('My Designs');
    await render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
